import { UseQueryResult } from '@tanstack/react-query';
import { warehouseApi } from 'src/api/warehouse';
import {
  PaginatedWarehouseProductsDto,
  WarehouseProductKind,
} from 'woofwoof-api';
import { warehouseRequestOptions } from '../utils/request';
import { useWarehouseQuery } from './useWarehouseQuery';

export interface WarehouseProductFilters {
  search: string;
  kind: '' | WarehouseProductKind;
  belowMinimum: boolean;
  negative: boolean;
  expiring: boolean;
  archived: boolean;
}

export const EMPTY_PRODUCT_FILTERS: WarehouseProductFilters = {
  search: '',
  kind: '',
  belowMinimum: false,
  negative: false,
  expiring: false,
  archived: false,
};

export interface WarehouseProductListParams {
  search?: string;
  kind?: WarehouseProductKind;
  belowMinimum?: boolean;
  negative?: boolean;
  expiring?: boolean;
  archived?: boolean;
  page: number;
  limit: number;
}

export function buildProductListParams(
  filters: WarehouseProductFilters,
  page: number,
  rowsPerPage: number,
): WarehouseProductListParams {
  const search = filters.search.trim();
  return {
    ...(search ? { search } : {}),
    ...(filters.kind ? { kind: filters.kind } : {}),
    ...(filters.belowMinimum ? { belowMinimum: true } : {}),
    ...(filters.negative ? { negative: true } : {}),
    ...(filters.expiring ? { expiring: true } : {}),
    ...(filters.archived ? { archived: true } : {}),
    page,
    limit: rowsPerPage,
  };
}

/** Key `['warehouse-products', organizationId, params]` (review S3). */
export function useWarehouseProducts(
  filters: WarehouseProductFilters,
  page: number,
  rowsPerPage: number,
): UseQueryResult<PaginatedWarehouseProductsDto> {
  const params = buildProductListParams(filters, page, rowsPerPage);
  return useWarehouseQuery(
    'warehouse-products',
    [params],
    async (organizationId, signal) =>
      (
        await warehouseApi.warehouseProductControllerList(
          params,
          warehouseRequestOptions(organizationId, signal),
        )
      ).data,
    { keepPreviousData: true },
  );
}

/** A first page is enough to pick from; typing narrows it (limit ≤ 100). */
export const PRODUCT_PICKER_LIMIT = 20;

/**
 * The receipt line editor's product search. Key `['warehouse-products',
 * organizationId, 'picker', params]` (preflight L-2), apart from the
 * Produkty list; active products only, like the list's default.
 */
export function useWarehouseProductPicker(
  search: string,
  enabled = true,
): UseQueryResult<PaginatedWarehouseProductsDto> {
  const params = buildProductListParams(
    { ...EMPTY_PRODUCT_FILTERS, search },
    0,
    PRODUCT_PICKER_LIMIT,
  );
  return useWarehouseQuery(
    'warehouse-products',
    ['picker', params],
    async (organizationId, signal) =>
      (
        await warehouseApi.warehouseProductControllerList(
          params,
          warehouseRequestOptions(organizationId, signal),
        )
      ).data,
    { enabled, keepPreviousData: true },
  );
}
